import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Search, X, BookOpen } from 'lucide-react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { AnimatePresence, motion } from 'framer-motion';
import duaData from './dualist.json';

const DuaExplorer = () => {
  const { isDarkMode } = useDarkMode();
  const [view, setView] = useState('categories'); // Base view is always 'categories'
  const [categories, setCategories] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedContext, setSelectedContext] = useState(null);
  const [filteredDuas, setFilteredDuas] = useState([]);
  const [selectedDua, setSelectedDua] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [allDuas, setAllDuas] = useState([]);

  // Sheet states
  const [contextSheetOpen, setContextSheetOpen] = useState(false);
  const [duaDetailSheetOpen, setDuaDetailSheetOpen] = useState(false);
  const [searchSheetOpen, setSearchSheetOpen] = useState(false);

  // Extract unique categories and contexts on component mount
  useEffect(() => {
    const uniqueCategories = [...new Set(duaData.map(dua => dua.category))];
    setCategories(uniqueCategories);
    setAllDuas(duaData);
  }, []);

  // Update contexts when a category is selected
  useEffect(() => {
    if (selectedCategory) {
      const categoryDuas = duaData.filter(dua => dua.category === selectedCategory);
      const uniqueContexts = [...new Set(categoryDuas.map(dua => dua.context))];
      setContexts(uniqueContexts);
    }
  }, [selectedCategory]);

  // Handle search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setIsSearching(false);
      if (searchSheetOpen) {
        setSearchSheetOpen(false);
      }
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase();
    const results = duaData.filter(dua => 
      dua.english.toLowerCase().includes(query) || 
      dua.context.toLowerCase().includes(query) || 
      dua.category.toLowerCase().includes(query) ||
      (dua.tags && dua.tags.some(tag => tag.toLowerCase().includes(query)))
    );
    setFilteredDuas(results);
    
    // If search has results, open the search sheet
    if (!searchSheetOpen) {
      setSearchSheetOpen(true);
    }
  }, [searchQuery]);

  const handleCategorySelect = (category) => {
    // Close any other sheets first
    closeAllSheets();
    
    // Set the category and open context sheet
    setSelectedCategory(category);
    setContextSheetOpen(true);
  };

  const handleContextSelect = (context) => {
    setSelectedContext(context);
    const contextDuas = duaData.filter(dua => dua.context === context);
    setFilteredDuas(contextDuas);
    
    // If there's only one dua in this context, go directly to the dua detail
    if (contextDuas.length === 1) {
      setSelectedDua(contextDuas[0]);
      setDuaDetailSheetOpen(true);
    }
  };

  const handleDuaSelect = (dua) => {
    setSelectedDua(dua);
    setDuaDetailSheetOpen(true);
  };

  const closeAllSheets = () => {
    setContextSheetOpen(false);
    setDuaDetailSheetOpen(false);
    setSearchSheetOpen(false);
  };

  const handleBack = () => {
    // Handle back navigation based on which sheets are open
    if (duaDetailSheetOpen) {
      setDuaDetailSheetOpen(false);
      setSelectedDua(null);
      return;
    }
    
    if (contextSheetOpen) {
      setContextSheetOpen(false);
      setSelectedCategory(null);
      setSelectedContext(null);
      setFilteredDuas([]);
      return;
    }
    
    if (searchSheetOpen) {
      setSearchSheetOpen(false);
      setSearchQuery('');
      setIsSearching(false);
      setFilteredDuas([]);
      return;
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setSearchSheetOpen(false);
    setFilteredDuas([]);
  };

  // Component for category/context item - now conditionally shows icon
  const ListItem = ({ text, onClick, isCategory = false }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center px-4 py-3.5 border-b transition-colors ${
        isDarkMode
          ? 'border-gray-800 active:bg-[#1A1A1A]'
          : 'border-gray-100 active:bg-gray-50'
      }`}
    >
      {/* Container for icon and text, ensures left alignment */}
      <div className="flex items-center flex-1 justify-start mr-2 min-w-0">
        {/* Conditionally render BookOpen icon for categories */}
        {isCategory && (
          <BookOpen className={`w-5 h-5 mr-3 flex-shrink-0 ${
            isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
          }`} />
        )}
        {/* Text remains left-aligned and can wrap */}
        <span className={`flex-1 text-left ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{text}</span>
      </div>
      {/* Chevron pushed to the right */}
      <ChevronRight className={`w-5 h-5 ml-auto flex-shrink-0 ${
        isDarkMode ? 'text-gray-600' : 'text-gray-400'
      }`} />
    </button>
  );

  // Component for dua item in the list
  const DuaItem = ({ dua, onClick }) => (
    <button
      onClick={onClick}
      className={`w-full flex flex-col items-start px-4 py-3.5 border-b transition-colors ${
        isDarkMode 
          ? 'border-gray-800 active:bg-[#1A1A1A]' 
          : 'border-gray-100 active:bg-gray-50'
      }`}
    >
      <div className="flex justify-between w-full">
        <span className={`font-arabic text-lg leading-normal text-right flex-1 mr-2 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {dua.arabic}
        </span>
        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${
          isDarkMode ? 'text-gray-600' : 'text-gray-400'
        }`} />
      </div>
      <span className={`mt-2 text-sm text-left ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        {dua.english.length > 80 ? `${dua.english.substring(0, 80)}...` : dua.english}
      </span>
    </button>
  );

  // Detailed view of a single dua
  const DuaDetail = ({ dua }) => (
    <div className={`p-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
      <div className="mb-6">
        <h2 className="text-lg font-medium mb-2 text-left">{dua.context}</h2>
        <p className={`text-sm text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Category: {dua.category}
        </p>
      </div>

      <div className="mb-6">
        <p className="font-arabic text-2xl leading-relaxed text-right mb-4 rtl">
          {dua.arabic}
        </p>
        <p className="text-sm font-medium italic mb-4 text-left">
          {dua.transliteration}
        </p>
        <p className="text-md text-left">
          {dua.english}
        </p>
      </div>

      <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-100'}`}>
        <h3 className="text-sm font-medium mb-2 text-left">Source</h3>
        <p className={`text-sm text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {dua.source.type}: {dua.source.reference}
          {dua.source.grading && ` (${dua.source.grading})`}
        </p>
      </div>

      {dua.usageNotes && (
        <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
          <h3 className={`text-sm font-medium mb-2 text-left ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Usage Notes</h3>
          <p className="text-sm text-left">
            {dua.usageNotes}
          </p>
        </div>
      )}

      {dua.tags && dua.tags.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-medium mb-2 text-left">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {dua.tags.map((tag, index) => (
              <span
                key={index}
                className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const SearchBar = () => (
    <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
      <div className={`flex items-center px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        <Search className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        <input
          type="text"
          placeholder="Search duas..."
          value={searchQuery}
          onChange={handleSearch}
          className={`ml-2 w-full outline-none border-none ${isDarkMode ? 'bg-transparent text-white placeholder-gray-500' : 'bg-transparent text-gray-900 placeholder-gray-500'}`}
        />
        {searchQuery && (
          <button onClick={clearSearch}>
            <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        )}
      </div>
    </div>
  );

  // Refactor header to match settings style
  const Header = ({ title, showBack }) => (
      <header className={`sticky top-0 z-40 ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
        <div style={{ paddingTop: 'calc(env(safe-area-inset-top))' }} className="px-4">
        <div className="h-16 relative flex items-center justify-center">
          {showBack && (
                <button 
                  onClick={handleBack}
              className={`p-2 rounded-full absolute left-0 top-1/2 -translate-y-1/2 ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
                >
                  <ChevronLeft className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
                </button>
              )}
          <h1 className={`text-2xl font-semibold text-center w-full truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h1>
        </div>
      </div>
          <SearchBar />
      </header>
  );

  // Calculate if any sheet is open for main sheet animation
  const isAnySheetOpen = contextSheetOpen || duaDetailSheetOpen || searchSheetOpen;

  return (
    <div className={`min-h-[100dvh] flex flex-col ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      {/* Main Sheet (Categories) */}
          <motion.div
        initial={false}
        animate={{ x: isAnySheetOpen ? '-15%' : 0 }}
        transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
        className="flex-1 flex flex-col fixed inset-0 z-30"
        style={{ pointerEvents: isAnySheetOpen ? 'none' : 'auto' }}
      >
        <Header title="Dua Explorer" showBack={false} />
        <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+72px)]">
          <div className="max-w-3xl mx-auto pt-4">
              <div className={`mx-4 rounded-xl overflow-hidden ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}>
                {categories.map((category, index) => (
                  <ListItem 
                    key={index} 
                    text={category} 
                    onClick={() => handleCategorySelect(category)} 
                    isCategory={true}
                  />
                ))}
              </div>
          </div>
        </main>
      </motion.div>

      {/* Context Sheet */}
      <AnimatePresence>
        {contextSheetOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: duaDetailSheetOpen ? '-15%' : 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            className={`fixed inset-0 z-40 flex flex-col ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}
            style={{ pointerEvents: duaDetailSheetOpen ? 'none' : 'auto' }}
          >
            <Header title={selectedCategory} showBack={true} />
            <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+72px)]">
              <div className="max-w-3xl mx-auto pt-4">
                <div className={`mx-4 rounded-xl overflow-hidden mb-4 ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}>
                  {contexts.map((context, index) => (
                    <ListItem 
                      key={index} 
                      text={context} 
                      onClick={() => handleContextSelect(context)} 
                    />
                  ))}
                </div>
                {filteredDuas.length > 0 && selectedContext && false && (
                  <div className={`mx-4 mt-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <h2 className="text-lg font-medium mb-3 text-left">Duas in {selectedContext}</h2>
                    <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}>
                      {filteredDuas.map((dua, index) => (
                        <DuaItem 
                          key={index} 
                          dua={dua} 
                          onClick={() => handleDuaSelect(dua)} 
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </main>
          </motion.div>
            )}
      </AnimatePresence>

      {/* Dua Detail Sheet */}
      <AnimatePresence>
        {duaDetailSheetOpen && selectedDua && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}
          >
            <header className={`sticky top-0 z-40 ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
              <div style={{ paddingTop: 'calc(env(safe-area-inset-top))' }} className="px-4">
                <div className="h-16 relative flex items-center justify-center">
                  <button
                    onClick={handleBack}
                    className={`p-2 rounded-full absolute left-0 top-1/2 -translate-y-1/2 ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
                  </button>
                  <h1 className={`text-2xl font-semibold text-center w-full truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Dua Details</h1>
                </div>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+72px)]">
              <div className={`mx-4 rounded-xl overflow-hidden mt-4 ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}> 
                <DuaDetail dua={selectedDua} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Results Sheet */}
      <AnimatePresence>
        {searchSheetOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            className={`fixed inset-0 z-40 flex flex-col ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}
            style={{ pointerEvents: duaDetailSheetOpen ? 'none' : 'auto' }}
          >
            <Header title="Search Results" showBack={true} />
            <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+72px)]">
              <div className="max-w-3xl mx-auto pt-4">
              <div className="mx-4">
                {filteredDuas.length > 0 ? (
                  <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}>
                    {filteredDuas.map((dua, index) => (
                      <DuaItem 
                        key={index} 
                        dua={dua} 
                        onClick={() => handleDuaSelect(dua)} 
                      />
                    ))}
                  </div>
                ) : (
                  <div className={`p-6 text-center rounded-xl ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      No duas found matching "{searchQuery}"
                    </p>
                  </div>
                )}
              </div>
              </div>
            </main>
          </motion.div>
        )}
        </AnimatePresence>
    </div>
  );
};

export default DuaExplorer; 